import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2, Edit2, X, Tag as TagIcon, Search } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useProject } from '../context/ProjectContext';
import { useConfirm } from '../context/ConfirmContext';
import { useDataVersion } from '../context/DataVersionContext';
import { formatDateBR } from '../lib/format';
import BotaoFechar, { useEscFecha } from '../components/BotaoFechar';

export default function TagsPage() {
  const { currentProject } = useProject();
  const confirm = useConfirm();
  const { bump, version } = useDataVersion();
  const [tags, setTags] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    if (!currentProject) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${currentProject.id}/tags_with_counts`);
      setTags(data);
    } finally { setLoading(false); }
  }, [currentProject]);
  useEffect(() => { load(); }, [load, version]);

  const save = async (data) => {
    try {
      if (editing) await api.put(`/tags/${editing.id}`, data);
      else await api.post(`/projects/${currentProject.id}/tags`, data);
      toast.success(editing ? 'Tag atualizada' : 'Tag criada');
      setShowForm(false); setEditing(null); await load(); bump();
    } catch (err) {
      toast.error('Erro ao salvar', { description: err?.response?.data?.detail || err.message });
    }
  };

  const remove = async (tag) => {
    const ok = await confirm({
      title: 'Excluir esta tag?',
      description: `${tag.name}${tag.transaction_count ? ` · vinculada a ${tag.transaction_count} ${tag.transaction_count === 1 ? 'transação' : 'transações'}` : ''}. A tag será removida das transações.`,
      confirmText: 'Excluir',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await api.delete(`/tags/${tag.id}`);
      toast.success('Tag excluída');
      await load(); bump();
    } catch (err) {
      toast.error('Erro ao excluir', { description: err?.response?.data?.detail || err.message });
    }
  };

  if (!currentProject) return <div className="text-slate-500 dark:text-slate-400">Selecione um projeto.</div>;

  const filtered = tags.filter((t) => !search.trim() || t.name.toLowerCase().includes(search.trim().toLowerCase()));

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-slate-100">Tags</h2>
          <p className="text-slate-500 dark:text-slate-400 mt-1">Organize e categorize suas transações com tags personalizadas</p>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }}
          className="flex items-center gap-2 px-5 py-3 bg-gradient-to-br from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white rounded-xl shadow-lg shadow-indigo-500/30 font-medium" data-testid="tags-create-btn">
          <Plus className="w-5 h-5" /> Criar Tag
        </button>
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar tags por nome..." className="input-md pl-10 pr-10" data-testid="tags-search-input" />
          {search && (
            <button type="button" onClick={() => setSearch('')} data-testid="tags-search-clear-icon"
              aria-label="Limpar busca" title="Limpar busca"
              className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded-md text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {search && (
          <button onClick={() => setSearch('')} data-testid="tags-search-clear"
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700">
            <X className="w-4 h-4" /> Limpar
          </button>
        )}
      </div>

      <div className="card overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-500 dark:text-slate-400">Carregando...</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-slate-500 dark:text-slate-400">
            {tags.length === 0 ? 'Nenhuma tag criada ainda. Clique em "Criar Tag" para começar.' : 'Nenhuma tag encontrada para essa busca.'}
          </div>
        ) : (
          <div className="divide-y divide-slate-100 dark:divide-slate-700">
            {filtered.map((tag) => (
              <div key={tag.id} className="group flex items-center gap-4 p-5 hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center shadow-md shrink-0">
                  <TagIcon className="w-6 h-6 text-white" strokeWidth={2.2} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 dark:text-slate-100 text-lg truncate">{tag.name}</div>
                  <div className="text-sm text-slate-500 dark:text-slate-400">
                    Criada em {formatDateBR(tag.created_at)} · {tag.transaction_count} {tag.transaction_count === 1 ? 'transação' : 'transações'}
                  </div>
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button title="Editar" onClick={() => { setEditing(tag); setShowForm(true); }}
                    className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400">
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button title="Excluir" onClick={() => remove(tag)}
                    className="p-2 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-900/30 text-rose-500">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showForm && (
        <TagFormModal initial={editing} onClose={() => { setShowForm(false); setEditing(null); }} onSave={save} />
      )}
    </div>
  );
}

function TagFormModal({ initial, onClose, onSave }) {
  const [name, setName] = useState(initial?.name || '');
  useEscFecha(onClose);
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">{initial ? 'Editar Tag' : 'Criar Tag'}</h3>
          <BotaoFechar onClose={onClose} testid="tag-form-fechar" />
        </div>
        <form onSubmit={(e) => { e.preventDefault(); if (name.trim()) onSave({ name: name.trim() }); }} className="space-y-3">
          <div>
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Nome da tag</label>
            <input value={name} onChange={(e) => setName(e.target.value)} required autoFocus
              placeholder="Ex: Mercado, Combustível, Lazer..." className="input" />
          </div>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700">
              Cancelar
            </button>
            <button type="submit"
              className="flex-1 px-4 py-2 bg-gradient-to-br from-indigo-600 to-indigo-700 text-white rounded-lg shadow-md font-medium">
              {initial ? 'Salvar' : 'Criar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
